import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import ActionsCell from "../../CustomizedTable/CustomCells/ActionsCell/ActionsCell";
import ContactConnectCell from "../../CustomizedTable/CustomCells/ContactConnectCell/ContactConnectCell";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import useCarrierTranslations from "../../../hooks/useCarrierTranslations/useCarrierTranslations";
import usePhoneTranslations from "../../../hooks/usePhoneTranslations/usePhoneTranslations";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function CarrierProfileContacts({ activeCarrier, handleTableAction }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const { roles } = useCarrierTranslations();
  const { phoneCategories } = usePhoneTranslations();
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const carrierContacts = useMemo(() => contacts, [contacts]);

  const columns = useMemo(
    () => [
      {
        Header: "Actions",
        id: "actions",
        activeCarrier: activeCarrier,
        actions: ["update-primary-contact", "edit-carrier-contact", "delete-carrier-contact"],
        width: 90,
        Cell: ActionsCell,
      },
      {
        Header: "Name",
        accessor: "name",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell,
      },
      {
        Header: "Role",
        accessor: "allRoles",
        filter: "fuzzyText",
        width: 156,
        Cell: DefaultCell
      },
      {
        Header: "Connect",
        width: 130,
        Cell: ContactConnectCell,
      },
      {
        Header: "Preferred",
        accessor: "primaryPhoneName",
        filter: "fuzzyText",
        width: 120,
        Cell: DefaultCell,
      },
      {
        Header: "Notes",
        accessor: "comments",
        filter: "fuzzyText",
        width: 300,
        Cell: DefaultCell,
      },
    ]
  );

  const parseRoles = contact => {
    let allRoles = "";

    Object.keys(roles).forEach((role => {
      if (contact[role]) {
        allRoles = allRoles.length ? allRoles + ", " + roles[role] : allRoles + roles[role];
      }
    }));

    return allRoles;
  };

  const parsePrimaryPhone = contact => {
    return phoneCategories[contact.primaryPhone];
  };

  const removeContact = updatedContact => {
    const index = contacts.findIndex(contact => contact.id === updatedContact.id);
    let updatedContacts = [...contacts];
    updatedContacts.splice(index, 1);
    setContacts(updatedContacts);
  };

  const updateContact = updatedContact => {
    updatedContact.allRoles = parseRoles(updatedContact);
    updatedContact.primaryPhoneName = parsePrimaryPhone(updatedContact);
    const index = contacts.findIndex(contact => contact.id === updatedContact.id);
    let updatedContacts = [...contacts];
    updatedContacts[index] = updatedContact;
    setContacts(updatedContacts);
  };

  const updateData = (contact) => {
    if (contact.isUpdate) {
      updateContact(contact);
    } else if (contact.isDelete) {
      removeContact(contact);
    }
  };

  useEffect(() => {
    if (activeCarrier.id) {
      setIsLoading(true);
      CarrierService.GetCarrierContacts(instance, activeCarrier.id).then(res => {
        let newContacts = res;

        newContacts.map(contact => {
          contact.allRoles = parseRoles(contact);
          contact.primaryPhoneName = parsePrimaryPhone(contact);
        });

        setContacts(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [activeCarrier]);

  return (
    <Box className="Carrier-profile-component">
      <Box className="Carrier-profile-component-header">
        <Typography variant="h3" color="black_opacity.100">
          Contacts
        </Typography>
        <Box sx={{ marginLeft: "auto" }}>
          <SecondaryButton
            label="Add"
            onClick={() => handleTableAction({ action: "carrier-contact", subaction: "add-contact", carrierId: activeCarrier.id })}
            startIcon={AddOutlinedIcon}
            size="small"
          />
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <CustomizedTable
          id="carrier-contacts"
          columns={columns}
          data={carrierContacts}
          updateData={updateData}
          actionCallback={handleTableAction}
        />
      )}
    </Box>
  );
}

CarrierProfileContacts.propTypes = {
  activeCarrier: PropTypes.object.isRequired,
  handleTableAction: PropTypes.func
};